import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Forgot from "./pages/Forgot";
import ResetPassword from "./pages/ResetPassword";
import Feed from "./pages/Feed";
import Marketplace from "./pages/Marketplace";
import Create from "./pages/Create";
import Listing from "./pages/Listing";
import CardDetail from "./pages/CardDetail";
import Collection from "./pages/Collection";
import Profile from "./pages/Profile";
import PublicProfile from "./pages/PublicProfile";
import Messages from "./pages/Messages";
import Orders from "./pages/Orders";
import SellerDisputes from "./pages/SellerDisputes";
import AdminDisputes from "./pages/AdminDisputes";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import AdminReports from "./pages/AdminReports";
import AdminVerifications from "./pages/AdminVerifications";
import AdminLogs from "./pages/AdminLogs";
import AdminWaitlist from "./pages/AdminWaitlist";
import AdminBlogs from "./pages/AdminBlogs";
import TradeHub from "./pages/TradeHub";
import LocalTrades from "./pages/LocalTrades";
import PostTrade from "./pages/PostTrade";
import PostBounty from "./pages/PostBounty";
import TradeOffer from "./pages/TradeOffer";
import OfferDetails from "./pages/OfferDetails";
import Settings from "./pages/Settings";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DisputePolicy from "./pages/DisputePolicy";
import IPDisclaimer from "./pages/IPDisclaimer";
import CommunityGuidelines from "./pages/CommunityGuidelines";
import SafetyCenter from "./pages/SafetyCenter";
import ChildSafety from "./pages/ChildSafety";
import ComingSoon from "./pages/ComingSoon";
import iconEvents from "./assets/icon-events.png";
import WhatIsCardex from "./pages/WhatIsCardex";
import ReportAbuse from "./pages/ReportAbuse";
import AccountDeletion from "./pages/AccountDeletion";
import NotFound from "./pages/NotFound";
import OnboardingPlan from "./pages/OnboardingPlan";

import ProtectedRoute from "./auth/ProtectedRoute";
import PublicRoute from "./auth/PublicRoute";
import { isAuthed } from "./auth/auth";
import { FEATURES } from "./config/features";

// Global route guard component for marketplace
function MarketplaceGuard({ children }: { children?: any }) {
  if (!FEATURES.MARKETPLACE_ENABLED) {
    return <ComingSoon />;
  }
  return children;
}

// Global route guard component for events/conventions
function EventsGuard({ children }: { children?: any }) {
  if (!FEATURES.EVENTS_ENABLED) {
    return (
      <ComingSoon
        title="Events & conventions coming soon"
        description="We are building a hub for TCG events so you can plan meetups and tournaments in one place."
        iconNode={<img src={iconEvents} alt="Events" />}
        ctaLabel="Back to Trading"
        ctaTo="/home"
        highlights={[
          "Browse upcoming conventions by location",
          "Save favorites and get reminders",
          "Add events to your calendar",
          "Get directions and venue details",
        ] as any}
      />
    );
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();

  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/" element={<Navigate to={isAuthed() ? "/home" : "/login"} replace />} />

      {/* Public */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot"
        element={
          <PublicRoute>
            <Forgot />
          </PublicRoute>
        }
      />
      {/* Reset link target — public, intentionally not behind PublicRoute so a
          valid recovery link still works even if a stale session is present. */}
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Legal Pages (Public) */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/community-guidelines" element={<CommunityGuidelines />} />
      <Route path="/dispute-policy" element={<DisputePolicy />} />
      <Route path="/ip-disclaimer" element={<IPDisclaimer />} />
      <Route path="/safety" element={<SafetyCenter />} />
      <Route path="/child-safety" element={<ChildSafety />} />
      <Route path="/what-is-cardex" element={<WhatIsCardex />} />
      <Route path="/report" element={<ReportAbuse />} />

      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Feed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/plan"
        element={
          <ProtectedRoute>
            <OnboardingPlan />
          </ProtectedRoute>
        }
      />

      {/* Protected - Marketplace (GATED) */}
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute>
            <MarketplaceGuard>
              <Marketplace />
            </MarketplaceGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <MarketplaceGuard>
              <Create />
            </MarketplaceGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/listing/:id"
        element={
          <ProtectedRoute>
            <MarketplaceGuard>
              <Listing />
            </MarketplaceGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <MarketplaceGuard>
              <Orders />
            </MarketplaceGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller-disputes"
        element={
          <ProtectedRoute>
            <MarketplaceGuard>
              <SellerDisputes />
            </MarketplaceGuard>
          </ProtectedRoute>
        }
      />

      {/* Protected - Profile & Messages */}
      <Route
        path="/collection"
        element={
          <ProtectedRoute>
            <Collection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/u/:username"
        element={
          <ProtectedRoute>
            <PublicProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account-deletion"
        element={
          <ProtectedRoute>
            <AccountDeletion />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:id"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />

      {/* Protected - Admin */}
      <Route
        path="/admin-disputes"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminDisputes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-reports"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-verifications"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminVerifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-logs"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-waitlist"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminWaitlist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-blogs"
        element={
          <ProtectedRoute requireRole="admin">
            <AdminBlogs />
          </ProtectedRoute>
        }
      />

      {/* Protected - Events */}
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <EventsGuard>
              <Events />
            </EventsGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:id"
        element={
          <ProtectedRoute>
            <EventsGuard>
              <EventDetail />
            </EventsGuard>
          </ProtectedRoute>
        }
      />
      <Route path="/conventions" element={<Navigate to="/events" replace />} />

      <Route
        path="/card/:cardId"
        element={
          <ProtectedRoute>
            <CardDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/local-trades"
        element={
          <ProtectedRoute>
            <TradeHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trades"
        element={
          <ProtectedRoute>
            <LocalTrades />
          </ProtectedRoute>
        }
      />
      <Route
        path="/post-trade"
        element={
          <ProtectedRoute>
            <PostTrade />
          </ProtectedRoute>
        }
      />
      <Route
        path="/post-bounty"
        element={
          <ProtectedRoute>
            <PostBounty />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trade-offer/:id"
        element={
          <ProtectedRoute>
            <TradeOffer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bounty-offer/:id"
        element={
          <ProtectedRoute>
            <TradeOffer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offer/:offerId"
        element={
          <ProtectedRoute>
            <OfferDetails />
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return <AppRoutes />;
}
